import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/api";

const LangSwitch = ({ prefix }) => {
  const { lang, setLang, t } = useLang();
  return (
    <div data-testid={`${prefix}-lang-switch`} role="group" aria-label={t.nav.langLabel} className="inline-flex border border-line font-mono text-[11px] uppercase tracking-[0.2em]">
      {["bg", "en"].map((l) => (
        <button
          key={l}
          data-testid={`${prefix}-lang-${l}`}
          aria-pressed={lang === l}
          onClick={() => setLang(l)}
          className={`px-3 py-2 transition-colors duration-300 ${lang === l ? "bg-gold text-ink" : "text-dim hover:text-mist"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, c } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled ? "bg-coal/70 backdrop-blur-xl saturate-150 border-line" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("#hero")} className="flex items-center hover:opacity-80 transition-opacity duration-300" aria-label={t.nav.toHome}>
          <img src="/logo.png" alt={t.nav.logoAlt} className="h-10 md:h-12 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {c.NAV_LINKS.map((l) => (
            <button key={l.href} data-testid={l.testid} onClick={() => go(l.href)} className="text-sm text-dim hover:text-mist transition-colors duration-300">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <LangSwitch prefix="nav" />
          </div>
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#contact")}
            className="hidden sm:inline-flex items-center border border-gold/60 text-gold px-5 py-2 text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-mist p-2" aria-label={t.nav.menu}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-coal/95 backdrop-blur-xl border-t border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {c.NAV_LINKS.map((l) => (
                <button key={l.href} data-testid={`mobile-${l.testid}`} onClick={() => go(l.href)} className="text-left text-mist text-lg font-serif">
                  {l.label}
                </button>
              ))}
              <div className="md:hidden pt-2">
                <LangSwitch prefix="mobile" />
              </div>
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => go("#contact")}
                className="mt-2 border border-gold/60 text-gold px-5 py-3 text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
